/**
 * PURPOSE: Grilla de saltos horizontales y lanzamientos (`SHL`).
 * RESPONSIBILITIES: Una fila por atleta y una columna por intento (`config.attempts`, 6 o 3). Cada
 *   celda acepta metros ("7.45"), "X" (nulo) o "-" (pasa) y, con `windMode: "attempt"`, su viento.
 *   Los intentos 4-6 solo se habilitan para quien el servidor marcó con `advancesToFinal` (o ya tiene
 *   intentos posteriores), salvo con `config.allAttempts` (martillo): ahí los 6 están siempre abiertos.
 *   Saltos con un solo resultado (`config.attempts: 1`): una única columna "Marca" y sin "Mejor".
 *   Mejor marca y lugar vienen del servidor.
 * COLLABORATORS: RoundEditor.jsx, gridCells.jsx, utils/athleticsRoundDraft.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/grids/FieldGrid.jsx
 */
import React from "react";

import {
  FIELD_STATUS_CODES,
  normalizeAttemptResult,
  normalizeWind,
  sanitizeWindTyping,
} from "../../../../utils/athleticsMarks.js";
import {
  attemptFieldPatch,
  attemptResultPatch,
  isAttemptEnabled,
  isServerInvalidCell,
} from "../../../../utils/athleticsRoundDraft.js";
import { ENTRY_STATUS_TITLES } from "../../labels.js";
import { EntryStatusChip, compactSelectClass } from "../ui.jsx";
import {
  CellInput,
  FrozenScroll,
  InstitutionCell,
  NumberCell,
  ParticipantCell,
  PlaceCell,
  RowActions,
  buildFrozenColumns,
  rowToneClass,
  tdClass,
  thCenterClass,
  thClass,
} from "./gridCells.jsx";

const frozen = buildFrozenColumns([
  ["order", 4],
  ["athlete", 12],
  ["institution", 5.5],
]);

const ATTEMPT_HELP = "Marca en metros (7.45; también 745), X = nulo, - = pasa.";
const LOCKED_HELP = "Intentos 4 a 6: solo para los 8 mejores tras el 3.er intento (lo indica el servidor al guardar).";
const SINGLE_MARK_HELP = "Marca final en metros (1.85 o 7.45; también 185). X = sin marca válida.";

/**
 * Estado manual (DNS/DQ) o "Auto": con "Auto" el servidor calcula OK / NM / NH a partir de los
 * intentos; ese estado calculado se muestra al lado como insignia.
 */
export const FieldStatusSelect = ({
  value,
  serverStatus,
  onChange,
  disabled,
  ariaLabel,
  codes = FIELD_STATUS_CODES,
}) => (
  <div className="flex items-center justify-center gap-1">
    <select
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value || null)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${compactSelectClass} w-20`}
      title="Auto: el servidor calcula el estado con los intentos"
    >
      <option value="">Auto</option>
      {codes.map((code) => (
        <option key={code} value={code} title={ENTRY_STATUS_TITLES[code]}>
          {code}
        </option>
      ))}
    </select>
    {!value && serverStatus ? <EntryStatusChip status={serverStatus} /> : null}
  </div>
);

const FieldGrid = ({
  prueba,
  round,
  draft,
  setRow,
  readOnly,
  localInvalidKeys,
  serverInvalid,
  changedIds,
  canRemove,
  rowActions,
  showWaPoints,
  attemptSlots,
  nav,
}) => {
  const entries = Array.isArray(round?.entries) ? round.entries : [];
  const windPerAttempt = prueba?.windMode === "attempt";
  const singleMark = attemptSlots === 1;
  const allAttempts = Boolean(prueba?.config?.allAttempts);
  const slots = singleMark ? 1 : Math.max(
    attemptSlots,
    ...entries.map((entry) => draft.rows[entry.id]?.attempts?.length || 0)
  );
  const columns = Array.from({ length: slots }, (_, index) => index);

  return (
    <FrozenScroll>
      <table className={`w-full ${singleMark ? "min-w-[560px]" : "min-w-[820px]"} table-auto`}>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th {...frozen("order", thCenterClass)}>Orden</th>
            <th {...frozen("athlete", thClass)}>Atleta</th>
            <th {...frozen("institution", thClass)} title="Universidad">Univ.</th>
            {columns.map((index) => (
              <th
                key={index}
                className={thCenterClass}
                title={singleMark ? SINGLE_MARK_HELP : index >= 3 && !allAttempts ? LOCKED_HELP : ATTEMPT_HELP}
              >
                {singleMark ? "Marca" : `Intento ${index + 1}`}
              </th>
            ))}
            {singleMark ? null : <th className={thCenterClass}>Mejor</th>}
            {showWaPoints ? <th className={thCenterClass}>Puntos WA</th> : null}
            <th className={thCenterClass}>Lugar</th>
            <th className={thCenterClass}>Estado</th>
            {canRemove ? <th className={thCenterClass} aria-label="Acciones" /> : null}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, rowIndex) => {
            const row = draft.rows[entry.id];
            if (!row) return null;
            const serverRow = serverInvalid?.rowId === entry.id;
            const name = entry.participant?.name || "atleta";
            return (
              <tr
                key={entry.id}
                className={`border-b border-slate-100 last:border-b-0 ${rowToneClass({
                  serverInvalid: serverRow,
                  changed: changedIds.has(entry.id),
                })}`}
              >
                <td {...frozen("order", `${tdClass} text-center`)}>
                  <NumberCell
                    value={row.order}
                    onChange={(value) => setRow(entry.id, { order: value })}
                    disabled={readOnly}
                    ariaLabel={`Orden de ${name}`}
                  />
                </td>
                <td {...frozen("athlete", tdClass)}>
                  <ParticipantCell participant={entry.participant} />
                  {entry.advancesToFinal && !allAttempts && !singleMark ? (
                    <span className="text-[10px] font-bold uppercase text-emerald-600">Pasa a 3 intentos más</span>
                  ) : null}
                </td>
                <td {...frozen("institution", tdClass)}>
                  <InstitutionCell institution={entry.participant?.institution} />
                </td>
                {columns.map((index) => {
                  const attempt = row.attempts[index] || { no: index + 1, result: "", wind: "" };
                  const enabled = isAttemptEnabled(entry, row, index, { allAttempts });
                  const disabled = readOnly || !enabled;
                  return (
                    <td key={index} className={`${tdClass} text-center`}>
                      <div className="flex flex-col items-center gap-1">
                        <CellInput
                          value={attempt.result}
                          onChange={(raw) => setRow(entry.id, (current) => attemptResultPatch(current, index, raw))}
                          onBlur={() =>
                            setRow(entry.id, (current) =>
                              attemptFieldPatch(
                                current,
                                index,
                                "result",
                                normalizeAttemptResult(current.attempts[index]?.result)
                              )
                            )
                          }
                          disabled={disabled}
                          invalid={isServerInvalidCell(serverInvalid, {
                            rowId: entry.id,
                            kind: "attempt",
                            cellValue: attempt.result,
                            attemptNo: index + 1,
                          })}
                          softInvalid={localInvalidKeys.has(`${entry.id}|attempt|${index}`)}
                          inputRef={nav.register(rowIndex, `a${index}`)}
                          onKeyDown={nav.onKeyDown(rowIndex, `a${index}`)}
                          placeholder={enabled ? "" : "—"}
                          title={singleMark ? SINGLE_MARK_HELP : enabled ? ATTEMPT_HELP : LOCKED_HELP}
                          ariaLabel={singleMark ? `Marca de ${name}` : `Intento ${index + 1} de ${name}`}
                          className="w-16"
                        />
                        {windPerAttempt ? (
                          <CellInput
                            value={attempt.wind}
                            onChange={(raw) =>
                              setRow(entry.id, (current) =>
                                attemptFieldPatch(current, index, "wind", sanitizeWindTyping(raw))
                              )
                            }
                            onBlur={() =>
                              setRow(entry.id, (current) =>
                                attemptFieldPatch(
                                  current,
                                  index,
                                  "wind",
                                  normalizeWind(current.attempts[index]?.wind) ?? ""
                                )
                              )
                            }
                            disabled={disabled}
                            invalid={isServerInvalidCell(serverInvalid, {
                              rowId: entry.id,
                              kind: "attemptWind",
                              cellValue: attempt.wind,
                              attemptNo: index + 1,
                            })}
                            softInvalid={localInvalidKeys.has(`${entry.id}|attemptWind|${index}`)}
                            inputRef={nav.register(rowIndex, `w${index}`)}
                            onKeyDown={nav.onKeyDown(rowIndex, `w${index}`)}
                            placeholder="viento"
                            title="Viento del intento: +1.2, -0.5, 0.0"
                            ariaLabel={`Viento del intento ${index + 1} de ${name}`}
                            className="w-16"
                            size="sm"
                          />
                        ) : null}
                      </div>
                    </td>
                  );
                })}
                {singleMark ? null : (
                  <td className={`${tdClass} text-center`}>
                    <span className="block text-sm font-black text-slate-900">{entry.best || "—"}</span>
                    {entry.bestWind ? <span className="text-[11px] text-slate-500">{entry.bestWind}</span> : null}
                  </td>
                )}
                {showWaPoints ? (
                  <td className={`${tdClass} text-center text-sm font-bold text-indigo-700`}>
                    {entry.waPoints ?? "—"}
                  </td>
                ) : null}
                <td className={`${tdClass} text-center`}>
                  <PlaceCell place={entry.placeInRound} />
                </td>
                <td className={`${tdClass} text-center`}>
                  <FieldStatusSelect
                    value={row.status}
                    serverStatus={entry.status}
                    onChange={(value) => setRow(entry.id, { status: value })}
                    disabled={readOnly}
                    ariaLabel={`Estado de ${name}`}
                  />
                </td>
                {canRemove ? (
                  <td className={`${tdClass} text-center`}>
                    <RowActions name={name} {...rowActions(entry)} />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </FrozenScroll>
  );
};

export default FieldGrid;
